import { useEffect, useState } from "react";
import { http, API } from "../api";
import { Users, House, CheckCircle2, AlertTriangle, MapPin, Megaphone, CalendarDays, ClipboardList, ArrowUpRight, Plus, ChevronRight, Building2, Share2, MessageCircle, Facebook, Twitter, Copy, Check, Cake, Send } from "lucide-react";
import Sparkline from "../Sparkline";
import { Modal } from "../shared";

function Stat({ label, value, trend, icon: Icon, tone, spark, sparkColor }) {
  return (
    <div className="stat" data-testid={`stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className={`stat-icon ${tone}`}><Icon size={19} /></div>
      <span>{label}</span>
      <strong>{value}</strong>
      <div className="stat-foot">
        <small className="trend">{trend}</small>
        {spark && <Sparkline data={spark} color={sparkColor} testid={`spark-${label.toLowerCase().replaceAll(" ", "-")}`} />}
      </div>
    </div>
  );
}

export default function Dashboard({ setPage, user }) {
  const [kpi, setKpi] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [events, setEvents] = useState([]);
  const [trends, setTrends] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bday, setBday] = useState(null);
  const [bdaySending, setBdaySending] = useState(false);
  const isAdmin = user && (user.role === "Super Admin" || user.role === "Admin");

  useEffect(() => {
    http.get("/dashboard").then((r) => setKpi(r.data));
    http.get("/contacts").then((r) => setContacts(r.data.slice(0, 4)));
    http.get("/events").then((r) => setEvents(r.data.slice(0, 3)));
    http.get("/dashboard/trends").then((r) => setTrends(r.data));
    if (user && (user.role === "Super Admin" || user.role === "Admin")) {
      http.get("/birthday/today").then((r) => setBday(r.data)).catch(() => {});
    }
  }, [user]);

  const sendBirthdayWishes = async () => {
    if (!bday || bday.count === 0) return;
    if (bday.already_run && !window.confirm(`Wishes were already sent today (${bday.last_run?.sent || 0} recipients). Send again?`)) return;
    setBdaySending(true);
    try {
      const r = await http.post("/birthday/send-today", { channel: "Both" });
      window.alert(`🎂 Sent ${r.data.sent} bilingual birthday wishes from ${r.data.sender?.en} / ${r.data.sender?.bn}${r.data.skipped ? ` (${r.data.skipped} skipped)` : ""}`);
      http.get("/birthday/today").then((r2) => setBday(r2.data));
    } catch (e) { window.alert(e.response?.data?.detail || "Send failed"); }
    finally { setBdaySending(false); }
  };

  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).toUpperCase();
  const storyUrl = `${API}/story/weekly`;
  const shareText = kpi
    ? `Ward 7 · Nabadwip this week: ${kpi.complaints_solved || 0} complaints solved, ${kpi.active_members || 0} active karyakartas. People-powered progress. #Nabadwip #Ward7`
    : "Ward 7 · Nabadwip weekly recap. People-powered progress. #Nabadwip #Ward7";
  const downloadStory = () => window.open(storyUrl, "_blank");
  const openShare = () => setShareOpen(true);
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + storyUrl)}`, "_blank");
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(storyUrl)}`, "_blank");
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(storyUrl)}&quote=${encodeURIComponent(shareText)}`, "_blank");
  const copyLink = async () => {
    const value = shareText + " " + storyUrl;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <section className="welcome">
        <div>
          <p className="eyebrow">{today}</p>
          <h1>Good morning, {user?.name?.split(" ")[0] || "there"}<span>.</span></h1>
          <p className="sub">Here&apos;s the pulse of Ward 7 and your team today.</p>
        </div>
        <div className="welcome-actions">
          {isAdmin && bday && bday.count > 0 && (
            <button className="dash-birthday-chip" data-testid="dash-birthday-chip" disabled={bdaySending} onClick={sendBirthdayWishes}
                    title={bday.already_run ? `Already sent to ${bday.last_run?.sent || 0} today` : ""}>
              <Cake size={14} /> {bdaySending ? "Sending…" : bday.already_run ? `🎂 ${bday.count} today · Resend` : `🎂 Send birthday wishes (${bday.count})`}
            </button>
          )}
          <button className="outline" data-testid="story-download-button" onClick={downloadStory}><Share2 size={17} />Weekly story</button>
          <button className="outline" data-testid="story-share-button" onClick={openShare}><Share2 size={17} />Share story</button>
          <button className="outline" data-testid="export-dashboard-button"><ArrowUpRight size={17} />Export report</button>
          <button className="primary" data-testid="add-record-button" onClick={() => setPage("people")}><Plus size={18} />Add record</button>
        </div>
      </section>

      <div className="stats">
        <Stat label="Families" value={kpi?.families ?? "—"} trend="Ward 7 households" icon={House} tone="teal"
          spark={trends?.people} sparkColor="#0F766E" />
        <Stat label="Individuals" value={kpi?.individuals ?? "—"} trend="Verified profiles growing" icon={Users} tone="blue"
          spark={trends?.people} sparkColor="#167389" />
        <Stat label="Coverage" value={kpi ? `${kpi.coverage}%` : "—"} trend="Verified / total" icon={CheckCircle2} tone="green"
          spark={trends?.turnout} sparkColor="#3a7647" />
        <Stat label="Open complaints" value={kpi?.complaints_open ?? "—"} trend={`${kpi?.complaints_solved ?? 0} solved`} icon={AlertTriangle} tone="amber"
          spark={trends?.complaints_new} sparkColor="#b77510" />
        <Stat label="Active members" value={kpi?.active_members ?? "—"} trend={`${kpi?.tasks_open ?? 0} open tasks`} icon={MapPin} tone="red"
          spark={trends?.visits} sparkColor="#b04a3f" />
      </div>

      <div className="dashboard-grid">
        <section className="panel activity-panel">
          <div className="panel-head">
            <div><h2>Field pulse</h2><p>Live activity across your constituency</p></div>
            <button data-testid="activity-filter-button">This week <ChevronRight size={15} /></button>
          </div>
          <div className="pulse-chart">
            <div className="chart-y"><span>200</span><span>150</span><span>100</span><span>50</span><span>0</span></div>
            <div className="bars">
              {[44, 62, 51, 72, 58, 91, 76, 68, 83, 70, 96, 80, 67, 88].map((h, i) => (
                <div key={i} className="bar-wrap">
                  <div className={`bar ${i === 10 ? "today" : ""}`} style={{ height: `${h}%` }} />
                  <small>{["M", "T", "W", "T", "F", "S", "S"][i % 7]}</small>
                </div>
              ))}
            </div>
          </div>
          <div className="legend">
            <span><i className="dot teal" />House visits <b>642</b></span>
            <span><i className="dot blue" />New profiles <b>186</b></span>
            <span><i className="dot amber" />Issues resolved <b>94</b></span>
          </div>
        </section>

        <section className="panel attention">
          <div className="panel-head">
            <div><h2>Needs attention</h2><p>Prioritized for today</p></div>
            <button data-testid="view-all-attention-button">View all <ArrowUpRight size={15} /></button>
          </div>
          {[
            { icon: AlertTriangle, title: `${kpi?.complaints_open ?? 0} open complaints`, text: "Need acknowledgement within 2 hours", tone: "red", to: "complaints" },
            { icon: ClipboardList, title: `${kpi?.tasks_open ?? 0} active tasks`, text: "Assigned to your ward team", tone: "amber", to: "tasks" },
            { icon: CalendarDays, title: `${kpi?.events_upcoming ?? 0} upcoming events`, text: "RSVPs open · Ward office", tone: "blue", to: "events" },
          ].map((x, i) => (
            <button className="attention-row" data-testid={`attention-item-${i}`} key={x.title} onClick={() => setPage(x.to)}>
              <span className={`row-icon ${x.tone}`}><x.icon size={17} /></span>
              <span><b>{x.title}</b><small>{x.text}</small></span>
              <ChevronRight size={16} />
            </button>
          ))}
        </section>
      </div>

      <div className="lower-grid">
        <section className="panel table-panel">
          <div className="panel-head">
            <div><h2>Recent people</h2><p>Latest activity in Ward 7</p></div>
            <button data-testid="view-people-button" onClick={() => setPage("people")}>View directory <ArrowUpRight size={15} /></button>
          </div>
          <div className="people-table">
            <div className="tr th"><span>Person</span><span>Ward / booth</span><span>Inclination</span><span>Profile</span></div>
            {contacts.map((p, i) => (
              <div className="tr" data-testid={`dashboard-person-row-${i}`} key={p.contact_id}>
                <span className="person">
                  <span className="avatar person-avatar">{p.name.split(" ").map((x) => x[0]).join("")}</span>
                  <span><b>{p.name}</b><small>{p.gender} · {p.profession}</small></span>
                </span>
                <span>Ward {p.ward} · Booth {p.booth}</span>
                <span><i className={`status-dot ${p.inclination === "Supporter" ? "green" : p.inclination === "Neutral" ? "blue" : p.inclination === "Opposition" ? "red" : "amber"}`} />{p.inclination}</span>
                <span><span className={`pill ${p.status === "Verified" ? "green" : "amber"}`}>{p.status}</span></span>
              </div>
            ))}
          </div>
        </section>

        <section className="panel map-panel">
          <div className="panel-head">
            <div><h2>Upcoming events</h2><p>Next {events.length} programs</p></div>
            <button data-testid="open-events-button" onClick={() => setPage("events")}>Open <ArrowUpRight size={15} /></button>
          </div>
          <div className="events-mini">
            {events.map((e, i) => (
              <div key={e.event_id} className="event-mini-row" data-testid={`dashboard-event-${i}`}>
                <div className="event-date"><CalendarDays size={16} /><small>{(e.starts_at || "").slice(0, 10)}</small></div>
                <div><b>{e.title}</b><small>{e.venue} · {e.registrations || 0} RSVPs</small></div>
              </div>
            ))}
            {!events.length && <p className="hint">No upcoming events</p>}
          </div>
        </section>
      </div>

      <div className="tiles">
        {[
          { icon: Building2, label: "Organizations", value: kpi?.organizations, to: "organizations" },
          { icon: Megaphone, label: "Campaigns", value: null, to: "campaigns" },
          { icon: CalendarDays, label: "Events planned", value: kpi?.events_upcoming, to: "events" },
          { icon: Users, label: "Volunteers", value: kpi?.active_members, to: "volunteers" },
        ].map((t, i) => (
          <button className="tile" data-testid={`tile-${t.to}`} key={t.label} onClick={() => setPage(t.to)}>
            <t.icon size={22} />
            <b>{t.value ?? "—"}</b>
            <small>{t.label}</small>
          </button>
        ))}
      </div>

      {shareOpen && (
        <Modal title="Share the progress" onClose={() => setShareOpen(false)} testid="share-modal">
          <p className="eyebrow">WEEKLY IMPACT</p>
          <p className="hint" style={{ marginTop: 6 }}>{shareText}</p>
          <img src={storyUrl} alt="Weekly story" style={{ width: "100%", borderRadius: 12, marginTop: 12, border: "1px solid var(--line)" }} />
          <div className="share-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
            <button className="outline" data-testid="share-whatsapp" onClick={shareWhatsApp} style={{ background: "#25D3661a", borderColor: "#25D366", color: "#128C3E" }}>
              <MessageCircle size={16} /> WhatsApp
            </button>
            <button className="outline" data-testid="share-twitter" onClick={shareTwitter} style={{ background: "#0000000a" }}>
              <Twitter size={16} /> X (Twitter)
            </button>
            <button className="outline" data-testid="share-facebook" onClick={shareFacebook} style={{ background: "#1877F21a", borderColor: "#1877F2", color: "#1877F2" }}>
              <Facebook size={16} /> Facebook
            </button>
            <button className="outline" data-testid="share-copy" onClick={copyLink}>
              {copied ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy link</>}
            </button>
          </div>
          <button className="primary" data-testid="share-download" onClick={downloadStory} style={{ width: "100%", marginTop: 10 }}>
            <ArrowUpRight size={16} /> Download image
          </button>
        </Modal>
      )}
    </>
  );
}
